import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  WORKFLOW_CATALOGUE,
  type WorkflowSettingsView,
  type WorkflowView,
} from "@dentacore/shared-types";

import {
  deleteWorkflow,
  listWorkflows,
  pauseWorkflows,
  runWorkflowNow,
  saveWorkflow,
  workflowSettings,
} from "../api/workflows";
import { useAuth } from "../auth/auth-context";
import { useActiveBranch } from "../api/use-active-branch";
import { useModules } from "../shell/modules-context";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { countLine, inputOf, triggerSummary } from "./graph-model";
import { RunLog, StatusPill } from "./run-log";
import { useWorkflowRefs, type WorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

/**
 * **Workflows** (STORY-224, WF-D6, WF-D10, WG-D14–WG-D17): the clinic's
 * workflows as cards — how each starts, who it is for, **a count of its steps
 * and never the steps themselves** (*"maybe a workflow has 100 steps"*), its
 * last run one line per record. Administrators switch one off, run it now,
 * edit, delete, and pause every workflow at once; everyone else reads.
 *
 * Only reachable while the clinic has the Workflows preview (WG-D15); the
 * server refuses every route without it, whatever this page draws.
 */
export function WorkflowsPage(): React.JSX.Element {
  const on = useModules().hasPreview("WORKFLOWS");
  const { user } = useAuth();
  const admin = user?.canAdminister === true;
  const active = useActiveBranch();
  const navigate = useNavigate();
  const refs = useWorkflowRefs();

  const [list, setList] = useState<WorkflowView[] | null>(null);
  const [settings, setSettings] = useState<WorkflowSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<WorkflowView | null>(null);
  const [log, setLog] = useState<WorkflowView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!on) return;
      Promise.all([listWorkflows(signal), workflowSettings(signal)])
        .then(([workflows, s]) => {
          setList(workflows);
          setSettings(s);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setError(cause instanceof Error ? cause.message : "Could not read the workflows");
        });
    },
    // The list is the active location's (who sees what is worked out there).
    [on, active],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Blank space: the header's *New workflow* (QR7), for whoever may make one.
  useRingProvider("page", () =>
    !on || !admin
      ? null
      : {
          ring: "page.workflows",
          title: "Workflows",
          meta: list === null ? undefined : `${String(list.length)} set up`,
          actions: [
            {
              key: "new",
              label: "New workflow",
              icon: RING_ICONS.plus,
              preview: "WORKFLOWS",
              hint: "Chain actions together",
              run: () => void navigate("/workflows/new"),
            },
          ],
        },
  );

  const act = async (key: string, work: () => Promise<string>): Promise<void> => {
    setBusy(key);
    setError(null);
    try {
      setNotice(await work());
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That did not work");
    } finally {
      setBusy(null);
    }
  };

  const toggle = (w: WorkflowView): void =>
    void act(`toggle:${w.id}`, async () => {
      await saveWorkflow(w.id, { ...inputOf(w), enabled: !w.enabled });
      return `“${w.name}” is ${w.enabled ? "off" : "on"}.`;
    });

  const runNow = (w: WorkflowView): void =>
    void act(`run:${w.id}`, async () => {
      const { started } = await runWorkflowNow(w.id);
      return started === 0
        ? `“${w.name}” found nothing to run on.`
        : `“${w.name}” started on ${String(started)} record${started === 1 ? "" : "s"}.`;
    });

  const pause = (paused: boolean): void =>
    void act("pause", async () => {
      setSettings(await pauseWorkflows(paused));
      return paused ? "Every workflow is paused." : "Workflows are running again.";
    });

  if (!on)
    return (
      <div className={styles.page} data-testid="workflows-page">
        <p className={styles.empty}>Workflows is not switched on for this clinic.</p>
      </div>
    );

  return (
    <div className={styles.page} data-testid="workflows-page">
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Workflows</h1>
          <p className={styles.lede}>
            A workflow chains actions you can already take, one after another. Each arrow says when
            to carry on.
          </p>
        </div>
        <span className={styles.grow} />
        {admin && settings !== null && (
          <label className={styles.switch} data-testid="wf-pause">
            <input
              type="checkbox"
              role="switch"
              checked={settings.paused}
              disabled={busy === "pause"}
              onChange={(event) => pause(event.target.checked)}
              data-testid="wf-pause-switch"
            />
            Pause all workflows
          </label>
        )}
        {admin ? (
          <button
            type="button"
            className={styles.primary}
            onClick={() => void navigate("/workflows/new")}
            data-testid="wf-new"
          >
            New workflow
          </button>
        ) : (
          <span className={styles.sub}>Only an administrator changes workflows.</span>
        )}
      </header>

      {settings?.paused === true && (
        <p className={styles.pausedBanner} role="status" data-testid="wf-paused-banner">
          Every workflow is paused
          {settings.pausedAt !== null
            ? ` since ${new Date(settings.pausedAt).toLocaleString(undefined, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : ""}
          . Nothing starts, and open runs wait, until an administrator switches them back on.
        </p>
      )}
      {error !== null && (
        <p className={styles.error} role="alert" data-testid="wf-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className={styles.notice} role="status" data-testid="wf-notice">
          {notice}
        </p>
      )}

      {list !== null && list.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No workflows yet</span>
          {admin
            ? "New workflow chains steps together: from the quick ring, when something changes, or on a schedule."
            : "When an administrator sets one up for your role, it shows here."}
        </div>
      )}

      <div className={styles.cards} data-testid="wf-list">
        {list?.map((w) => (
          <WorkflowCard
            key={w.id}
            workflow={w}
            admin={admin}
            refs={refs}
            busy={busy}
            onToggle={() => toggle(w)}
            onRun={() => runNow(w)}
            onEdit={() => void navigate(`/workflows/${w.id}`)}
            onDelete={() => setDeleting(w)}
            onLog={() => setLog(w)}
          />
        ))}
      </div>

      {deleting !== null && (
        <ConfirmDialog
          title={`Delete “${deleting.name}”?`}
          confirmLabel="Delete"
          cancelLabel="Keep it"
          tone="danger"
          busy={busy === "delete"}
          onConfirm={() => {
            const w = deleting;
            setDeleting(null);
            void act("delete", async () => {
              await deleteWorkflow(w.id);
              return `“${w.name}” deleted.`;
            });
          }}
          onCancel={() => setDeleting(null)}
          testId="wf-confirm-delete"
        >
          It stops running, and its run log goes with it.
        </ConfirmDialog>
      )}
      {log !== null && (
        <RunLog workflowId={log.id} name={log.name} admin={admin} onClose={() => setLog(null)} />
      )}
    </div>
  );
}

function WorkflowCard({
  workflow: w,
  admin,
  refs,
  busy,
  onToggle,
  onRun,
  onEdit,
  onDelete,
  onLog,
}: {
  workflow: WorkflowView;
  admin: boolean;
  refs: WorkflowRefs;
  busy: string | null;
  onToggle: () => void;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onLog: () => void;
}): React.JSX.Element {
  const ring = w.trigger.kind === "ring";
  return (
    <article
      className={w.enabled ? styles.card : `${styles.card} ${styles.cardOff}`}
      data-testid="wf-card"
      data-workflow-id={w.id}
    >
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>{w.name}</h2>
        <label className={styles.switch}>
          <input
            type="checkbox"
            role="switch"
            checked={w.enabled}
            disabled={!admin || busy === `toggle:${w.id}`}
            onChange={onToggle}
            data-testid="wf-card-toggle"
          />
          {w.enabled ? "On" : "Off"}
        </label>
      </header>

      <div className={styles.chips}>
        <span className={`${styles.tag} ${styles.tagTrigger}`}>
          {triggerSummary(w.subject, w.trigger, refs.money)}
        </span>
        {w.roleIds.length === 0 ? (
          <span className={styles.tag}>Every role</span>
        ) : (
          w.roleIds.map((id) => (
            <span key={id} className={styles.tag}>
              {refs.roleName(id)}
            </span>
          ))
        )}
        <span className={styles.tag}>
          {w.allBranches
            ? "Every location"
            : w.branchIds.length === 1
              ? refs.branchName(w.branchIds[0] as string)
              : `${String(w.branchIds.length)} locations`}
        </span>
        {!ring && <span className={styles.tag}>Runs as {refs.roleName(w.runAsRoleId)}</span>}
      </div>

      <p className={styles.count} data-testid="wf-card-count">
        <b>{countLine(w)}</b>
        {w.openRuns > 0 && (
          <span className={styles.sub}>
            {" "}
            · {w.openRuns} run{w.openRuns === 1 ? "" : "s"} open
          </span>
        )}
      </p>

      {w.lastRuns.length > 0 && (
        <details className={styles.last}>
          <summary>
            Last run{" "}
            {new Date(w.lastRuns[0]?.startedAt ?? "").toLocaleString(undefined, {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </summary>
          <ul className={styles.lines}>
            {w.lastRuns.map((r) => (
              <li key={r.id}>
                <b>{r.recordName}</b> <StatusPill status={r.status} />
                {r.outcome !== null && <span className={styles.sub}>{r.outcome}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <footer className={styles.cardFoot}>
        {admin && (
          <>
            <button
              type="button"
              className={styles.ghost}
              onClick={onEdit}
              data-testid="wf-card-edit"
            >
              Edit
            </button>
            <button
              type="button"
              className={styles.ghost}
              onClick={onDelete}
              data-testid="wf-card-delete"
            >
              Delete
            </button>
          </>
        )}
        <button type="button" className={styles.quiet} onClick={onLog} data-testid="wf-card-runs">
          Run log
        </button>
        <span className={styles.grow} />
        {ring ? (
          <span className={styles.sub}>
            Ctrl + click {WORKFLOW_CATALOGUE[w.subject].label} to run it
          </span>
        ) : (
          admin && (
            <button
              type="button"
              className={styles.ghost}
              disabled={busy === `run:${w.id}`}
              onClick={onRun}
              data-testid="wf-card-run"
            >
              Run now
            </button>
          )
        )}
      </footer>
    </article>
  );
}
